import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useTranslation } from "react-i18next";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { getUniqueCurrencies } from "../../utils/currencyOptions";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import JalaliDateField from "../Components/JalaliDateField";

const belongsToShop = (record, shopId) => {
  if (!shopId) return true;
  const recordShopId =
    record?.shop ??
    record?.shop_id ??
    record?.shop_details?.id ??
    record?.shop_data?.id;
  return recordShopId != null && String(recordShopId) === String(shopId);
};

const getStockProduct = (stock) =>
  stock?.product_details || stock?.purchase_item_details?.product || null;

const getStockUnit = (stock) =>
  stock?.unit_details || stock?.purchase_item_details?.unit_details || null;

const getStockOptionLabel = (stock) => {
  const product = getStockProduct(stock);
  return product?.name || "";
};

const getStockOptionKey = (stock) => {
  const product = getStockProduct(stock);
  const productId = product?.id;
  const productName = getStockOptionLabel(stock);
  return productId != null
    ? `product-${productId}`
    : `name-${String(productName).trim().toLowerCase()}`;
};

const SaleEdit = ({ open, onClose, editData, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/sales", token);

  // Local state for sale-level fields.
  const [shop, setShop] = useState("");
  const [customer, setCustomer] = useState("");
  const [discount, setDiscount] = useState("");
  const [saleDate, setSaleDate] = useState("");
  const [saleCurrency, setSaleCurrency] = useState("");
  const [rent, setRent] = useState("");
  const [loader, setLoader] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});

  // Fetch dropdown data.
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop/?page_size=all",
    token
  );
  const { data: customers, loading: customerLoading } = useFetchObjects(
    ["customer"],
    "shop/customers?page_size=all",
    token
  );
  // Products may be used for fallback, but primary product options come from stocks.
  const { loading: productLoading } = useFetchObjects(
    ["product"],
    "shop/products?page_size=all",
    token
  );
  const { data: units, loading: unitLoading } = useFetchObjects(
    ["unit"],
    "shop/units/?page_size=all",
    token
  );
  const { data: currencies, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies/?page_size=all",
    token
  );

  const saleId = open && editData?.id ? String(editData.id) : "";
  const { data: saleDetailData } = useFetchObjects(
    ["sale", "detail", saleId],
    saleId ? `shop/sales/${saleId}/` : null,
    token,
    {
      enabled: Boolean(saleId),
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
    }
  );

  // Fetch stocks for the selected shop.
  const [stocks, setStocks] = useState([]);
  const {
    data: stocksData,
    loading: stockLoading,
    refetch: refetchStocks,
  } = useFetchObjects(
    ["stock", "sale", shop],
    shop ? `shop/stocks/?shop=${shop}&page_size=all` : null,
    token
  );

  const shopsList = shopsData?.results ?? shopsData ?? [];
  const customersList = customers?.results ?? customers ?? [];
  const unitsList = units?.results ?? units ?? [];
  const currenciesSource = currencies?.results ?? currencies ?? [];
  const branchCustomers = useMemo(
    () => customersList.filter((customer) => belongsToShop(customer, shop)),
    [customersList, shop]
  );
  const branchUnits = useMemo(
    () => unitsList.filter((unit) => belongsToShop(unit, shop)),
    [unitsList, shop]
  );
  const branchCurrencies = useMemo(
    () =>
      getUniqueCurrencies(
        currenciesSource.filter((currency) => belongsToShop(currency, shop))
      ),
    [currenciesSource, shop]
  );

  const availableStocks = React.useMemo(() => {
    const stocksList = Array.isArray(stocks) ? stocks : [];
    const uniqueStocks = [];
    const seen = new Set();

    stocksList.forEach((stock) => {
      const optionKey = getStockOptionKey(stock);
      if (!optionKey || seen.has(optionKey)) return;
      seen.add(optionKey);
      uniqueStocks.push(stock);
    });

    return uniqueStocks;
  }, [stocks]);

  useEffect(() => {
    if (stocksData) {
      const list = stocksData?.results ?? stocksData;
      setStocks(Array.isArray(list) ? list : []);
    } else {
      setStocks([]);
    }
  }, [stocksData]);

  useEffect(() => {
    if (shop) {
      refetchStocks();
    } else {
      setStocks([]);
    }
  }, [shop, refetchStocks]);

  // Populate initial values when editData or open changes (so we reset when dialog opens with new data).
  useEffect(() => {
    const sourceData = saleDetailData || editData;
    if (open && sourceData) {
      const shopId = sourceData.shop != null ? sourceData.shop : sourceData.shop_details?.id;
      const customerId = sourceData.customer != null ? sourceData.customer : sourceData.customer_details?.id;
      const currencyId = sourceData.currency != null ? sourceData.currency : sourceData.currency_details?.id;

      setShop(shopId != null ? String(shopId) : "");
      setCustomer(customerId != null ? String(customerId) : "");

      // Load amount directly
      setDiscount(sourceData.amount != null && sourceData.amount !== "" ? String(sourceData.amount) : "");
      setRent(sourceData.rent != null && sourceData.rent !== "" ? String(sourceData.rent) : "");
      setLoader(sourceData.loader != null && sourceData.loader !== "" ? String(sourceData.loader) : "");

      setSaleDate(
        sourceData.sale_date ? String(sourceData.sale_date).substring(0, 10) : ""
      );
      setSaleCurrency(currencyId != null ? String(currencyId) : "");
      setReceivedAmount(sourceData.received_amount != null ? String(sourceData.received_amount) : "");
      setNotes(sourceData.notes != null ? String(sourceData.notes) : "");
      if (sourceData.sale_items?.length) {
        const initialItems = sourceData.sale_items.map((item) => ({
          id: item.id,
          product: (item.product != null ? item.product : item.product_details?.id) != null ? String(item.product != null ? item.product : item.product_details.id) : "",
          stock_record_id: "",
          quantity: item.quantity,
          original_quantity: item.quantity,
          original_product: (item.product != null ? item.product : item.product_details?.id) != null ? String(item.product != null ? item.product : item.product_details.id) : "",
          unit: (item.unit != null ? item.unit : item.unit_details?.id) != null ? String(item.unit != null ? item.unit : item.unit_details.id) : "",
          sale_price: item.sale_price,
          available: item.product_details?.stock_quantity ?? "",
        }));
        setItems(initialItems);
      } else {
        setItems([]);
      }
    }
  }, [open, editData, saleDetailData]);

  useEffect(() => {
    if (!availableStocks.length) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.stock_record_id) return item;
        const matchedStock = availableStocks.find((stock) => {
          const stockProductId = String(getStockProduct(stock)?.id || "");
          const stockUnitId = String(getStockUnit(stock)?.id || "");
          return stockProductId === String(item.product || "") && stockUnitId === String(item.unit || "");
        });
        return matchedStock
          ? {
              ...item,
              stock_record_id: String(matchedStock.id),
              available: matchedStock.quantity,
            }
          : item;
      })
    );
  }, [availableStocks]);

  // When a product is selected, look up its stock record and auto-fill unit, sale_price, and available quantity.
  const handleProductSelect = (index, stockId) => {
    const newItems = [...items];
    newItems[index].stock_record_id = stockId;
    const stockRecord = stocks.find(
      (stock) => String(stock.id) === String(stockId)
    );
    if (stockRecord) {
      const selectedProductId = String(getStockProduct(stockRecord)?.id || "");
      newItems[index].product = selectedProductId;
      newItems[index].unit = stockRecord.unit_details
        ? String(stockRecord.unit_details.id)
        : "";
      newItems[index].sale_price = stockRecord.sale_price;
      newItems[index].available = stockRecord.quantity;
      newItems[index].original_quantity =
        newItems[index].original_product === selectedProductId
          ? newItems[index].original_quantity
          : "";
      newItems[index].buy_price = stockRecord.buy_price || stockRecord.purchase_item_details?.buy_price || "";
      newItems[index].wholesale_price = stockRecord.wholesale_price || stockRecord.purchase_item_details?.wholesale_price || "";
      newItems[index].retail_price = stockRecord.retail_price || stockRecord.purchase_item_details?.retail_price || stockRecord.purchase_item_details?.sale_price || stockRecord.sale_price || "";
    } else {
      newItems[index].product = "";
      newItems[index].stock_record_id = "";
      newItems[index].unit = "";
      newItems[index].sale_price = "";
      newItems[index].available = "0";
      newItems[index].buy_price = "";
      newItems[index].wholesale_price = "";
      newItems[index].retail_price = "";
    }
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        quantity: "",
        original_quantity: "",
        original_product: "",
        stock_record_id: "",
        unit: "",
        sale_price: "",
        available: "",
        buy_price: "",
        wholesale_price: "",
        retail_price: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleShopChange = (value) => {
    setShop(value);
    setCustomer("");
    setSaleCurrency("");
    setDiscount("");
    setRent("");
    setLoader("");
    setItems([]);
    setErrors({});
  };

  const totalAmount = items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.sale_price) || 0;
    return acc + qty * price;
  }, 0);

  const discountValue = parseFloat(discount) || 0;
  const rentValue = parseFloat(rent) || 0;
  const loaderValue = parseFloat(loader) || 0;
  const finalTotal = totalAmount - discountValue + rentValue + loaderValue;

  const itemsValid =
    items.length > 0 &&
    items.every((item) => item.product && item.quantity && item.sale_price);

  const validate = () => {
    let newErrors = {};
    if (!shop) newErrors.shop = t("sale.shopRequired") || "Branch is required";
    if (!customer)
      newErrors.customer = t("sale.customerRequired") || "Customer is required";
    if (!saleDate)
      newErrors.saleDate =
        t("sale.saleDateRequired") || "Sale date is required";
    if (!saleCurrency)
      newErrors.currency = t("sale.currencyRequired") || "Currency is required";
    if (items.length === 0) {
      newErrors.items =
        t("sale.atLeastOneItemRequired") ||
        "At least one sale item is required";
    } else {
      items.forEach((item, idx) => {
        if (!item.product)
          newErrors[`item_${idx}_product`] =
            t("sale.productRequired") || "Product is required";
        if (!item.unit)
          newErrors[`item_${idx}_unit`] =
            t("sale.unitRequired") || "Unit is required";
        if (!item.quantity)
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityRequired") || "Quantity is required";
        else if (
          parseFloat(item.quantity) >
          parseFloat(item.available || "0") +
            (item.original_product === item.product
              ? parseFloat(item.original_quantity || "0")
              : 0)
        )
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityExceedsAvailable") ||
            "Quantity exceeds available stock";
        if (!item.sale_price)
          newErrors[`item_${idx}_salePrice`] =
            t("sale.salePriceRequired") || "Sale price is required";
      });
    }
    if (receivedAmount !== "" && parseFloat(receivedAmount) > finalTotal) {
      newErrors.receivedAmount =
        t("sale.receivedExceedsTotal") ||
        "Received amount should not exceed total amount";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = {
      shop,
      customer,
      amount: discountValue,
      rent: rentValue,
      loader: loaderValue,
      received_amount: receivedAmount,
      sale_date: saleDate,
      currency: saleCurrency,
      notes: notes.trim() || undefined,
      items,
    };
    handleUpdate(editData.id, payload);
  };

  useEffect(() => {
    if (isSuccess) {
      setShop("");
      setCustomer("");
      setDiscount("");
      setRent("");
      setLoader("");
      setSaleDate("");
      setSaleCurrency("");
      setReceivedAmount("");
      setNotes("");
      setItems([]);
      setFormError("");
      onClose();
    }
  }, [isSuccess, onClose]);

  // Get unit title from ID
  const getUnitTitle = (unitId) => {
    const unit = branchUnits.find(u => String(u.id) === String(unitId));
    return unit ? unit.title : "";
  };

  // Format product with size only (without unit)
  const formatProductWithSize = (product) => {
    if (!product) return "";
    let displayName = product.name || "";
    if (product.size) {
      displayName += ` - ${product.size}`;
    }
    return displayName;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" dir={fieldDirection}>
      <DialogTitle>{t("sale.editSale")}</DialogTitle>
      <DialogContent>
        {formError && (
          <Typography color="error" sx={{ mb: 2, textAlign: fieldTextAlign }}>
            {formError}
          </Typography>
        )}
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2, direction: fieldDirection }}
        >
          {/* Sale-level fields arranged in two columns */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.selectShop")}
                fullWidth
                required
                value={shop}
                onChange={(e) => handleShopChange(e.target.value)}
                disabled={shopLoading}
                error={Boolean(errors.shop)}
                helperText={errors.shop}
                SelectProps={selectProps}
                {...getRtlTextFieldProps(formDirection)}
              >
                {shopsList.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.selectCustomer")}
                fullWidth
                required
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                disabled={!shop || customerLoading}
                error={Boolean(errors.customer)}
                helperText={errors.customer}
                SelectProps={selectProps}
                {...getRtlTextFieldProps(formDirection)}
              >
                {branchCustomers.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <JalaliDateField
                mode="date"
                label={t("sale.saleDate")}
                fullWidth
                required
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                error={Boolean(errors.saleDate)}
                helperText={errors.saleDate}
                {...getRtlTextFieldProps(formDirection, { InputLabelProps: { shrink: true } })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.currency")}
                fullWidth
                required
                value={saleCurrency}
                onChange={(e) => setSaleCurrency(e.target.value)}
                disabled={!shop || currencyLoading}
                error={Boolean(errors.currency)}
                helperText={errors.currency}
                SelectProps={selectProps}
                {...getRtlTextFieldProps(formDirection)}
              >
                {branchCurrencies.map((curr) => (
                  <MenuItem key={curr.id} value={String(curr.id)}>
                    {curr.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("sale.discount")}
                type="number"
                fullWidth
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                error={Boolean(errors.discount)}
                helperText={errors.discount}
                {...getRtlTextFieldProps(formDirection)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("sale.rent", "Rent")}
                type="number"
                fullWidth
                value={rent}
                onChange={(e) => setRent(e.target.value)}
                {...getRtlTextFieldProps(formDirection)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("sale.loader", "Loader")}
                type="number"
                fullWidth
                value={loader}
                onChange={(e) => setLoader(e.target.value)}
                {...getRtlTextFieldProps(formDirection, {
                  inputProps: { min: 0, step: "0.01" },
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("sale.note")}
                fullWidth
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("sale.notePlaceholder")}
                {...getRtlTextFieldProps(formDirection)}
              />
            </Grid>
          </Grid>
          {/* Sale Items Section */}
          <Box sx={{ mt: 2, direction: fieldDirection }}>
            <Typography variant="h6" sx={{ textAlign: fieldTextAlign }}>{t("sale.saleItems")}</Typography>
            {errors.items && (
              <Typography color="error" variant="body2" sx={{ textAlign: fieldTextAlign }}>
                {errors.items}
              </Typography>
            )}
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                  direction: fieldDirection,
                }}
              >
                {/* Row 1: Product select and Remove icon */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <TextField
                    select
                    label={t("sale.selectProduct")}
                    required
                    value={item.stock_record_id}
                    onChange={(e) => handleProductSelect(index, e.target.value)}
                    fullWidth
                    disabled={!shop || productLoading || stockLoading}
                    error={Boolean(errors[`item_${index}_product`])}
                    helperText={errors[`item_${index}_product`]}
                    SelectProps={selectProps}
                    {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                  >
                    {availableStocks.map((stock) => (
                      <MenuItem key={stock.id} value={String(stock.id)}>
                        {getStockOptionLabel(stock)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton onClick={() => removeItem(index)} color="error">
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Box>
                {/* Row 2: All sale item fields in one row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, direction: fieldDirection }}>
                  <TextField
                    label={t("sale.availableQuantity")}
                    value={item.available || ""}
                    InputProps={{ readOnly: true }}
                    {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                  />
                  <TextField
                    label={t("sale.quantity")}
                    type="number"
                    required
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(index, "quantity", e.target.value)
                    }
                    error={Boolean(errors[`item_${index}_quantity`])}
                    helperText={errors[`item_${index}_quantity`]}
                    {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                  />
                  {/* Unit field - Auto-filled and READ-ONLY */}
                  <TextField
                    label={t("sale.selectUnit")}
                    required
                    value={getUnitTitle(item.unit)}
                    InputProps={{ readOnly: true }}
                    error={Boolean(errors[`item_${index}_unit`])}
                    helperText={errors[`item_${index}_unit`]}
                    {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                  />
                  <TextField
                    label={t("sale.salePrice")}
                    type="number"
                    required
                    value={item.sale_price}
                    onChange={(e) =>
                      handleItemChange(index, "sale_price", e.target.value)
                    }
                    error={Boolean(errors[`item_${index}_salePrice`])}
                    helperText={errors[`item_${index}_salePrice`]}
                    {...getRtlTextFieldProps(formDirection, { sx: { flex: 1 } })}
                  />
                </Box>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              disabled={!shop}
              sx={{ mt: 1 }}
            >
              {t("sale.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2, textAlign: fieldTextAlign }}>
            <Typography variant="body1">
              {t("sale.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountValue > 0 && (
              <Typography variant="body1">
                {t("sale.discountAmount")}: -{discountValue.toFixed(2)}
              </Typography>
            )}
            {rentValue > 0 && (
              <Typography variant="body1">
                {t("sale.rent", "Rent")}: +{rentValue.toFixed(2)}
              </Typography>
            )}
            {loaderValue > 0 && (
              <Typography variant="body1">
                {t("sale.loader", "Loader")}: +{loaderValue.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("sale.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>
          </Box>
          {/* Received Amount at the end */}
          <Box sx={{ mt: 2, direction: fieldDirection }}>
            <TextField
              label={t("sale.receivedAmount")}
              type="number"
              fullWidth
              value={receivedAmount}
              onChange={(e) => setReceivedAmount(e.target.value)}
              error={Boolean(errors.receivedAmount)}
              helperText={errors.receivedAmount}
              {...getRtlTextFieldProps(formDirection)}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("sale.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("sale.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaleEdit;
